/**
 * Stringpruefung (Kapitel H).
 *
 * Der Rechenweg steht wortgleich in der Spec, samt durchgerechnetem Beispiel:
 * Modul mit Uoc 38,5 V, Temperaturkoeffizient -0,25 %/K, Auslegung -10 Grad,
 * Wechselrichter mit 1000 V. 23 Module ergeben 963 V (zulaessig), 24 Module
 * 1005 V (zu viel). Genau das laeuft beim Zeichnen mit.
 */
export const DEFAULT_AUSLEGUNG = {
    kaltGradC: -10,
    warmGradC: 70,
    referenzGradC: 25,
};
/**
 * Korrekturfaktor der Spannung bei abweichender Temperatur.
 * Bei -0,25 %/K und -10 Grad: 1 + (-0,0025) * (-35) = 1,0875.
 *
 * Der Koeffizient der Spannung ist bei jedem kristallinen Modul negativ. Wer
 * das Minus beim Abtippen vergisst, bekäme bei Kälte eine kleinere Spannung
 * und damit einen zu langen String — deshalb zählt nur der Betrag.
 */
export function temperaturfaktor(tkProzentProK, gradC, referenzGradC = 25) {
    return 1 - (Math.abs(tkProzentProK) / 100) * (gradC - referenzGradC);
}
/** Leerlaufspannung eines Strings bei der angegebenen Temperatur. */
export function stringSpannung(anzahlModule, modul, gradC, referenzGradC = 25) {
    return (anzahlModule * modul.uocV * temperaturfaktor(modul.tkUocProzentProK, gradC, referenzGradC));
}
/** Wie viele Module duerfen hoechstens in Reihe (PV-H03). */
export function maxModuleProString(modul, maxEingangsspannungV, auslegung = DEFAULT_AUSLEGUNG) {
    const jeModul = modul.uocV *
        temperaturfaktor(modul.tkUocProzentProK, auslegung.kaltGradC, auslegung.referenzGradC);
    if (jeModul <= 0)
        throw new Error('maxModuleProString: Modulspannung muss positiv sein');
    return Math.floor(maxEingangsspannungV / jeModul);
}
/** Wie viele Module mindestens, damit der String im Sommer im MPP-Bereich bleibt (PV-H04). */
export function minModuleProString(modul, mppUntergrenzeV, auslegung = DEFAULT_AUSLEGUNG) {
    if (modul.umppV === undefined)
        return undefined;
    const jeModul = modul.umppV *
        temperaturfaktor(modul.tkUocProzentProK, auslegung.warmGradC, auslegung.referenzGradC);
    if (jeModul <= 0)
        return undefined;
    return Math.ceil(mppUntergrenzeV / jeModul);
}
/** Ein String durchpruefen. Warnung, kein Verbot — das entscheidet der Planer. */
export function pruefeString(anzahlModule, modul, wr, auslegung = DEFAULT_AUSLEGUNG) {
    const spannungKaltV = stringSpannung(anzahlModule, modul, auslegung.kaltGradC, auslegung.referenzGradC);
    const max = maxModuleProString(modul, wr.maxEingangsspannungV, auslegung);
    const min = wr.mppUntergrenzeV !== undefined
        ? minModuleProString(modul, wr.mppUntergrenzeV, auslegung)
        : undefined;
    const spannungWarmMppV = modul.umppV !== undefined
        ? anzahlModule *
            modul.umppV *
            temperaturfaktor(modul.tkUocProzentProK, auslegung.warmGradC, auslegung.referenzGradC)
        : undefined;
    const runde = (v) => v.toFixed(0);
    if (spannungKaltV > wr.maxEingangsspannungV) {
        return {
            befund: 'zu_lang',
            anzahlModule,
            spannungKaltV,
            ...(spannungWarmMppV !== undefined ? { spannungWarmMppV } : {}),
            maxModule: max,
            ...(min !== undefined ? { minModule: min } : {}),
            meldung: `${anzahlModule} Module ergeben bei ${auslegung.kaltGradC} Grad ${runde(spannungKaltV)} V ` +
                `und überschreiten die zulässigen ${runde(wr.maxEingangsspannungV)} V. ` +
                `Höchstens ${max} Module.`,
        };
    }
    if (min !== undefined && anzahlModule < min) {
        return {
            befund: 'zu_kurz',
            anzahlModule,
            spannungKaltV,
            ...(spannungWarmMppV !== undefined ? { spannungWarmMppV } : {}),
            maxModule: max,
            minModule: min,
            meldung: `${anzahlModule} Module bleiben im Sommer unter dem MPP-Bereich des Wechselrichters. ` +
                `Mindestens ${min} Module.`,
        };
    }
    return {
        befund: 'ok',
        anzahlModule,
        spannungKaltV,
        ...(spannungWarmMppV !== undefined ? { spannungWarmMppV } : {}),
        maxModule: max,
        ...(min !== undefined ? { minModule: min } : {}),
    };
}
//# sourceMappingURL=elektrik.js.map